export const AUTHENTICATION_REQUIRED_EVENT = 'nwm-oss:authentication-required';

export interface AuthenticationRequiredMessage {
  readonly type: typeof AUTHENTICATION_REQUIRED_EVENT;
  readonly returnTo?: string;
}

let notificationInProgress = false;

/**
 * Notify the active OIDC host that the server rejected the current session.
 * A 403 is deliberately not handled here: it means the user is authenticated
 * but is not authorized for the requested operation.
 */
export function notifyAuthenticationRequired(status: number): void {
  if (status !== 401 || typeof window === 'undefined' || notificationInProgress) {
    return;
  }

  notificationInProgress = true;
  const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  const message: AuthenticationRequiredMessage = {
    type: AUTHENTICATION_REQUIRED_EVENT,
    returnTo,
  };

  window.dispatchEvent(new CustomEvent(AUTHENTICATION_REQUIRED_EVENT, { detail: message }));

  if (window.parent !== window) {
    const parentOrigin = readParentOrigin();
    if (parentOrigin) {
      window.parent.postMessage(message, parentOrigin);
    }
  }
}

export function isAuthenticationRequiredMessage(value: unknown): value is AuthenticationRequiredMessage {
  return Boolean(
    value && typeof value === 'object' && (value as { type?: unknown }).type === AUTHENTICATION_REQUIRED_EVENT,
  );
}

function readParentOrigin(): string | null {
  const configuredOrigin = new URLSearchParams(window.location.search).get('shellOrigin');
  if (configuredOrigin) {
    try {
      return new URL(configuredOrigin).origin;
    } catch {
      return null;
    }
  }

  if (!document.referrer) return null;
  try {
    return new URL(document.referrer).origin;
  } catch {
    return null;
  }
}

/** Test-only reset for the process-wide redirect deduplication guard. */
export function resetAuthenticationRequiredNotification(): void {
  notificationInProgress = false;
}
